import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { catchError, Observable, Subject, takeUntil, throwError } from 'rxjs';
import { FollowupSummary } from 'src/app/shared/models/followup-summary.model';
import { FollowupTime } from 'src/app/shared/models/followup-time.model';
import { ControlMasks } from 'src/app/shared/validators/control-masks';
import { FollowUpViewBase } from 'src/app/shared/models/followup-base-view.model';
import { PerDiemFollowupView } from 'src/app/shared/models/perdiem-followup-view.model';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { Store } from '@ngrx/store';
import { RecruitingFollowUpService } from 'src/app/recruiting/recruiting.service';
import { PerDiemRecruitingService } from 'src/app/perdiem-recruiting/perdiem-recruiting.service';
import dayjs from 'dayjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CustomValidators } from 'src/app/shared/helpers/custom-validators';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export const datePickerValidator = '^[0-3][0-9]/[0-3][0-9]/(?:[0-9][0-9])?[0-9][0-9]$';

export const defaultTime = [
    {
        id: 0,
        followupTime: '12:00 AM',
        followupTimeSort: 5
    }
];

export interface PriorityEntry {
    value: string;
    name: string;
    icon: string;
    class: string;
    type: PriorityType;
}

export enum PriorityType {
    Low = 1,
    Med = 2,
    High = 3
}

export enum FollowPriority {
    Low = 20,
    Med = 21,
    High = 40
}

export function convertToDate(str: string) {
    str = str.replace(/\//gi, '-');

    const y = /\-([\d]{4})/.exec(str)[1],
        d = /\-([\d]{2}|[\d])/.exec(str)[1],
        m = /([\d]{2}|[\d])\-/.exec(str)[1],
        H = /\s([\d]{2}|[\d]):/.exec(str)[1],
        i = /:([\d]{2})/.exec(str)[1],
        AMorPM = /(AM|PM)/.exec(str)[1];

    return new Date(`${y}-${m}-${d} ${H}:${i} ${AMorPM}`);
}

@UntilDestroy()
@Component({
    selector: 'followup-picker-base',
    template: '<div></div>',
    styles: []
})
export abstract class FollowupPickerBaseComponent implements OnInit, OnChanges, OnDestroy {
    @Input() currentFollowup: FollowUpViewBase;
    @Input() canEdit: boolean;
    @Output() onChangeFollowup: EventEmitter<any> = new EventEmitter();

    resourcesForm: UntypedFormGroup;
    currentState = 'initial';
    hours: FollowupTime[] = defaultTime;
    showCalendar = false;
    recruiterId: number;
    selectedDates = [];
    currentDate: Date = new Date();
    priorityList: PriorityEntry[] = [
        {
            value: 'High',
            name: 'High Priority',
            icon: 'arrow_upward',
            class: 'high-red',
            type: PriorityType.High
        },
        {
            value: 'Med',
            name: 'Medium Priority',
            icon: 'arrow_upward',
            class: 'med-yellow',
            type: PriorityType.Med
        },
        {
            value: 'Low',
            name: 'Low Priority',
            icon: 'arrow_downward',
            class: 'low-green',
            type: PriorityType.Low
        }
    ];

    // tracks the number of followups per day for calendar pulldown for the current month
    followupsByDay: FollowupSummary[] = [];
    priority: PriorityEntry = this.priorityList[2];
    hour: FollowupTime = this.hours[0];
    saveInProgress = false;
    changeInProgress = false;
    shortDateMask = ControlMasks.shortDateFourDigitYear;
    readonly featureFlag = FeatureFlag;
    protected readonly _cleanup$ = new Subject();

    constructor(
        private readonly store: Store,
        protected readonly _featureManager: LDFeatureManager
    ) {}

    abstract ngOnInit(): void;

    abstract ngOnChanges(changes: SimpleChanges): void;

    ngOnDestroy(): void {
        this._cleanup$.next(true);
        this._cleanup$.complete();
    }

    /**
     * Change priority type from dropdown
     * @param e: PriorityEntry
     */
    selectionPriorityChange(e: PriorityEntry): void {
        this.priority = e;
        this.resourcesForm.controls.priorityList.setValue(e);
        if (this.currentFollowup?.priority) {
            this.currentFollowup.priority = e.type;
        }
    }

    protected abstract selectedDateChanged(newDate: any, e2: any): void;

    protected abstract initLookups(shouldSkipGetFollowupSummary: boolean): void;

    protected handleSelectedDateChanged(newDate: Date, service: RecruitingFollowUpService) {
        if (newDate.getMonth() !== this.currentDate.getMonth()) {
            this.getLookups(newDate, service, false);
        }
        this.currentDate = newDate;
    }

    /**
     * Toggle calendar view
     */
    toggleCalendar(e?: MouseEvent): void {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        this.showCalendar = !this.showCalendar;
    }

    /**
     * Event from calendar to set new date
     * @param date: string
     */
    updateDate(date: string): void {
        this.resourcesForm.controls.date.markAsDirty();
        this.selectedDates = [];
        this.toggleCalendar();
        this.resourcesForm.controls.date.patchValue(date);
        this.selectedDates.push(new Date(this.resourcesForm.value.date));
    }

    /**
     * Tracking clicking outside;
     */
    clickOutside(): void {
        this.toggleCalendar();
    }

    hasError(controlName: string, errorName: string) {
        return this.resourcesForm.controls[controlName].hasError(errorName);
    }

    protected getLookups(
        fuDate: Date,
        service: RecruitingFollowUpService,
        shouldSkipGetFollowupSummary: boolean
    ): void {
        this.followupsByDay = [];

        if (shouldSkipGetFollowupSummary) {
            this.getFollowUpTimes();
        } else {
            service
                .getRecruiterFollowupSummary(
                    this.recruiterId,
                    this.getLookupsDateStart(fuDate),
                    this.getLookupsDateEnd(fuDate)
                )
                .subscribe((data) => {
                    this.followupsByDay = data;
                    this.getFollowUpTimes();
                });
        }
    }

    protected abstract getFollowUpTimes(): void;

    protected setFollowUpTimes(time: FollowupTime[]): void {
        this.hours = time;
        this.hour = this.hours[0];
        this.resourcesForm.get('time').patchValue(this.hour);
    }

    protected getLookupsDateStart(fuDate: Date): Date {
        const dateStart = new Date(fuDate.getFullYear(), fuDate.getMonth(), 1);
        dateStart.setHours(0, 0, 0, 0);
        return dateStart;
    }

    protected getLookupsDateEnd(fuDate: Date): Date {
        const dateEnd = new Date(fuDate.getFullYear(), fuDate.getMonth() + 1, 0);
        dateEnd.setHours(23, 59, 59, 999);
        return dateEnd;
    }

    /**
     * Set priority days;
     * @param arrayDays: Date[]
     * @param day: Date
     */
    setDate(arrayDays: Date[], day: Date): void {
        arrayDays.push(new Date(new Date(day).getFullYear(), new Date(day).getMonth(), new Date(day).getDate()));
    }

    initForm(): void {
        const validators: ValidatorFn[] = [Validators.required, Validators.pattern(datePickerValidator)];
        validators.push(CustomValidators.minDate('01/01/1900'));
        this.resourcesForm = new UntypedFormGroup({
            priorityList: new UntypedFormControl(this.priorityList[2]),
            date: new UntypedFormControl('', validators),
            time: new UntypedFormControl(this.hours[0]),
            followupReason: new UntypedFormControl('', [Validators.maxLength(50)])
        });
        this.checkCanEdit();
    }

    protected checkCanEdit() {
        if (this.canEdit) {
            this.resourcesForm?.enable();
        } else {
            this.resourcesForm?.disable();
        }
    }

    abstract saveFollowUp(markComplete?: boolean): void;

    protected updateRecruiterResources(
        service: RecruitingFollowUpService,
        follow: FollowUpViewBase,
        markComplete = false
    ): void {
        service
            .updateRecruiterResources(follow)
            .pipe(
                takeUntil(this._cleanup$),
                catchError((e: HttpErrorResponse) => this.catchSaveError(e))
            )
            .subscribe((data) => this.handleSave(data, markComplete));
    }

    protected abstract handleSave(data: FollowUpViewBase, markComplete?: boolean): void;

    protected catchSaveError(e: HttpErrorResponse): Observable<never> {
        const message = this.getErrorMessage(e, false);
        this.store.dispatch(showToasterMessage({ message: message }));
        this.saveInProgress = false;
        return throwError(() => new Error(message));
    }

    protected createPerDiemFollowUpDto(followUpId?: number): PerDiemFollowupView {
        const dateWithTimeStr = `${this.resourcesForm.value.date} ${this.resourcesForm.value.time.followupTime}`;
        const date = dayjs(dateWithTimeStr).format('YYYY-MM-DD HH:mm');

        return {
            id: followUpId,
            priority: this.resourcesForm.value.priorityList.type,
            followupDate: date,
            recruiterId: this.recruiterId,
            candidateId: this.currentFollowup.candidateId,
            followupReason: this.resourcesForm.value.followupReason
        } as PerDiemFollowupView;
    }

    protected abstract createDto(markComplete?: boolean): FollowUpViewBase;

    public resetDefaultValues(): void {
        const defualtPriority = this.priorityList[2];
        this.priority = defualtPriority;
        this.resourcesForm.controls.priorityList.setValue(defualtPriority);
        this.resourcesForm.controls.date.clearValidators();
        this.resourcesForm.controls.date.setValue('');
        this.resourcesForm.controls.time.setValue(this.hours[0]);
        this.resourcesForm.controls.followupReason.setValue('');
    }
    abstract openMarkAsCompleteDialog(): void;

    requestMarkAsComplete(service: PerDiemRecruitingService, followUp: PerDiemFollowupView): void {
        service
            .markAsComplete(followUp)
            .pipe(
                takeUntil(this._cleanup$),
                catchError((e: HttpErrorResponse) => {
                    const message = this.getErrorMessage(e, true);
                    this.store.dispatch(showToasterMessage({ message: message }));
                    return throwError(() => new Error(message));
                })
            )
            .subscribe(() => this.markAsComplete());
    }

    getErrorMessage(e: HttpErrorResponse, isMarkComplete = false): string {
        if (e?.status === 403) {
            return "You must be this candidate's recruiter to do this";
        } else {
            return isMarkComplete
                ? 'Error occurred while marking followup as complete'
                : 'Error occurred while saving followup';
        }
    }

    protected abstract markAsComplete(): void;

    private _isLdFeatureFlagEnabled(featureName: FeatureFlag): boolean {
        let isFeatureEnabled = false;
        this._featureManager
            .isEnabled(featureName)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                isFeatureEnabled = flagIsEnabled;
            });
        return isFeatureEnabled;
    }
}
